import { useCallback, useRef } from 'react';
import { latestRoundSignatures } from '@safetyhub/shared';
import type { Report } from '../../../lib/reports';

/**
 * Promise-based in-flight dedup
 *
 * 동일한 비동기 작업이 진행 중일 때 추가 호출이 들어오면 같은 Promise를 반환하여
 * 중복 실행을 차단한다. WS 이벤트가 짧은 시간 내 여러 번 발화해도 fetch는 한 번만 수행.
 *
 * 마지막 이벤트 누락 위험: 진행 중인 fetch가 끝난 뒤 도착한 이벤트는 새 fetch를 트리거하므로
 * "버스트 dedup" + "최신 상태 반영"이 함께 보장된다 (setTimeout debounce와 달리).
 */
export function useInFlightDedup(fn: () => Promise<void>): () => Promise<void> {
  const inFlightRef = useRef<Promise<void> | null>(null);
  return useCallback(() => {
    if (inFlightRef.current) return inFlightRef.current;
    const promise = fn().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, [fn]);
}

/**
 * setReport 병합 헬퍼
 *
 * 정책:
 * 1. updatedAt 가드: 수신한 updated.updatedAt이 prev.updatedAt보다 과거면 stale 간주하고 prev 유지
 *    → 늦게 도착한 WS refetch가 optimistic 최신 상태를 덮어쓰는 것 방지
 * 2. null/undefined 구분: updated의 필드가 undefined면 prev 값 보존, 명시적 null은 존중하여 덮어씀
 *    → 서버 응답에 include 누락으로 필드가 빠진 경우(undefined)는 prev 유지,
 *       서버가 "없음"으로 명시적 null 설정한 경우(의도적 삭제)는 반영
 *    → latestApprovalSignatures, approvalLines 등 부분 응답으로 누락 시 UI 소실 방지
 */
export function mergeReportUpdate(prev: Report | null, updated: Report): Report {
  if (!prev) return updated;

  if (prev.updatedAt && updated.updatedAt) {
    const prevTime = new Date(prev.updatedAt).getTime();
    const updatedTime = new Date(updated.updatedAt).getTime();
    if (Number.isFinite(prevTime) && Number.isFinite(updatedTime) && updatedTime < prevTime) {
      return prev;
    }
  }

  const merged: Record<string, unknown> = { ...prev };
  for (const key of Object.keys(updated) as Array<keyof Report>) {
    const value = updated[key];
    if (value !== undefined) {
      merged[key as string] = value;
    }
  }
  return merged as unknown as Report;
}

/**
 * 최신 라운드의 결재 서명만 필터링 — shared `latestRoundSignatures`의 `undefined` 허용 껍데기.
 * 재제출 시 이전 라운드의 서명은 이력으로 보존되므로 최신 라운드만 표시. 판정(round `?? 1`)은
 * shared가 소유한다(라운드 6에서 web 6곳의 `||1`/`??1` 혼재를 한 정본으로 접음).
 */
export function filterLatestRoundSignatures<T extends { round?: number | null }>(
  signatures: readonly T[] | undefined,
): T[] {
  if (!signatures || signatures.length === 0) return [];
  return latestRoundSignatures(signatures);
}

/** 재전송 모달이 **참조로** 보여줄 반려 원문. */
export interface LatestRejection {
  comment: string;
  signerName?: string | null;
  signedAt?: string | null;
}

/**
 * 가장 최근 반려의 원문을 뽑는다 (P3/S45).
 *
 * 위임자는 이 글을 **보면서 자기 말로 다시 쓴다** — 그래서 참조이지 입력 초기값이 아니다.
 * 종전에는 구 상세가 이 값을 `initialMessage`로 **자동 채웠고**, 그러면 결재자가 내부 언어로
 * 쓴 문장을 그대로 외부인에게 전송할 위험이 생긴다(확정안이 없앤 바로 그 경로).
 *
 * ⚠️ 두 상세(구/COMPOSED)가 같은 파생을 쓰도록 한 곳에 둔다 — 예전에 한쪽만 자동 채워 두
 * 화면의 동작이 갈렸다.
 */
export function pickLatestRejection(
  signatures: Array<{ status?: string; comment?: string | null; signerName?: string | null; signedAt?: string | null }> | undefined,
): LatestRejection | undefined {
  const latest = (signatures ?? [])
    .filter((sig) => sig.status === 'REJECTED' && !!sig.comment?.trim())
    .sort((a, b) => {
      const aTime = a.signedAt ? new Date(a.signedAt).getTime() : 0;
      const bTime = b.signedAt ? new Date(b.signedAt).getTime() : 0;
      return bTime - aTime;
    })[0];

  if (!latest) return undefined;
  return { comment: latest.comment!.trim(), signerName: latest.signerName, signedAt: latest.signedAt };
}
